import { ServiceLocator } from '../../../app/ServiceLocator';
import { Messages } from '../../../codegen/Localize';
import { ISiteRoleFilter } from '../../../ts/filters/SiteRoleFilter';
import { UserErrorCode } from '../../../ts/helpers/ErrorCodes';
import { L10n } from '../../../ts/L10n/L10n';
import { IUserInfo } from '../../../ts/models/IUserInfo';
import { IErrorItem, MultipleItemResult } from '../../../ts/models/MultipleItemResult';
import { INamedItemInfo } from '../../../ts/models/resourceInfoParts';
import { Set } from '../../../ts/models/Set';
import { LicensingTier, SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { IResult } from '../../../ts/ServerApi/types/IResponse';
import { ContentActionNotification, IMultipleResultNotificationOptions, MultipleResultNotificationOptions } from '../../../ts/services/ContentActionNotification';
import { NotificationHelper } from '../../components/helpers/NotificationHelper';
import { LicenseTierFilter } from '../filters/LicenseTierFilter';


// Notification For:
// => site role change for single site

export class SiteRolesUpdateActionNotification {

  public static readonly instance = new SiteRolesUpdateActionNotification();

  public notify(
    result: IResult,
    users: Set<IUserInfo>,
    siteRoleEnum: string) {

    const contentActionNotification = ServiceLocator.get<ContentActionNotification>('ContentActionNotification');
    const siteRoleOptionsResource = SiteRoleOptionsResource.instance;
    const $filter = ServiceLocator.get<ng.IFilterService>('$filter');
    const translatedSiteRole = $filter<ISiteRoleFilter>('siteRole')(siteRoleEnum);

    const successfulName = NotificationHelper.getSuccessfulName(users, result);
    const failingName = NotificationHelper.getFailingName(users, result);

    // create a notification object for result reporting
    const notification: IMultipleResultNotificationOptions = MultipleResultNotificationOptions.create(
      new MultipleItemResult(users, result),
      {
        describeSuccessSummary: (successCount: number) => {
          return Messages.updateSiteRoleSuccess({
            SUCCESS_COUNT: successCount,
            USER_NAME: L10n.elideIfLong(successfulName),
            SITE_ROLE: translatedSiteRole,
            SUCCESS_COUNT_LOC: L10n.localizedCount(successCount)});
        },
        describeErrorSummary: (errorCount: number) => {
          return Messages.updateSiteRoleError({
            ERROR_COUNT: errorCount,
            USER_NAME: L10n.elideIfLong(failingName),
            SITE_ROLE: translatedSiteRole,
            ERROR_COUNT_LOC: L10n.localizedCount(errorCount)
          });
        },
        describeErrorDetail: (errorItem: IErrorItem<IUserInfo>) => {
          return {
            itemName: errorItem.item.username,
            itemUrl: NotificationHelper.getUrlForUser(<IUserInfo>errorItem.item),
            errorMessage: this.errorMessageFor(errorItem, siteRoleOptionsResource.getSiteRoleOptionBySiteRole(siteRoleEnum).licenseTierRole, translatedSiteRole)
          };
        }
      }
    );

    // pass the notification object to the service for displaying
    contentActionNotification.notify(notification);
  }

  private errorMessageFor(errorItem: IErrorItem<INamedItemInfo>, licenseTier: LicensingTier, translatedSiteRole: string) {
    const translatedLicenseRole = LicenseTierFilter.filter(licenseTier);
    switch (errorItem.error.code) {
      case UserErrorCode.UserCannotHaveSiteRoleLowerThanHighestMinimumLulGroupRole:
        return Messages.errorUserCannotHaveSiteRoleLowerThan_highestMinimumLulGroupRole({SITE_ROLE: translatedSiteRole});
      case UserErrorCode.UserInsufficientLicenses:
        return Messages.errorUserInsufficientLicensesInTier_updateSiteRole({LICENSE_TIER: translatedLicenseRole});
      case UserErrorCode.UserNotSupportedGuestOperation:
        return Messages.errorUserNotSupportedGuestOperation();
      case UserErrorCode.UserNotSupportedAdminOperation:
        return Messages.errorUserNotSupportedAdminOperation();
      case UserErrorCode.UserNotSupportedSelfOperation:
        return Messages.errorUserNotSupportedSelfOperation();
      case UserErrorCode.UserInsufficientSiteQuota:
        return Messages.errorUserInsufficientSiteQuota();
      case UserErrorCode.UserCannotChangeFromSupportUser:
        return Messages.errorUserCannotChangeFromSupportUser();
      default:
        return Messages.errorUnknown();
    }
  }
}

export default SiteRolesUpdateActionNotification.instance;
